import { useCallback, useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Undo2, Redo2, X, GripVertical } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { TaskRow } from "./TaskRow";
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = BACKEND_URL;
const URGENCIES = [
  { label: "Action Imm\u00e9diate", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800" },
  { label: "Priorit\u00e9 Haute", color: "bg-[#e7f0e9] text-[#3f6947] dark:bg-[rgba(46,109,59,0.22)] dark:text-[#4d8a5b] border-[#bfd1c3] dark:border-[#2e6d3b]" },
  { label: "Action Continue", color: "bg-[#eef4ef] text-[#4c7e55] dark:bg-[rgba(46,109,59,0.24)] dark:text-[#2e6d3b] border-[#bfd1c3] dark:border-[#2e6d3b]" },
  { label: "Moyen Terme", color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700" },
];

function uid() { return crypto.randomUUID(); }

export default function ConsignesView({ title = "Consignes", cmsTab = "Consignes", resourcePath = null, testId = "consignes-view" }) {
  const [sections, setSections] = useState([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showNewSection, setShowNewSection] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newFamily, setNewFamily] = useState("");
  const [validationError, setValidationError] = useState("");
  const bottomRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const headers = useCallback(() => {
    return { "Content-Type": "application/json" };
  }, []);
  const apiBase = resourcePath || (cmsTab === "Consignes" ? `${API}/api/consignes` : `${API}/api/cms/${encodeURIComponent(cmsTab)}`);

  const applyResponse = (data) => {
    setSections(data.sections || []);
    setCanUndo(data.canUndo ?? false);
    setCanRedo(data.canRedo ?? false);
  };

  const load = useCallback(async () => {
    const res = await fetch(apiBase, { headers: headers() });
    if (res.ok) applyResponse(await res.json());
  }, [apiBase, headers]);

  useEffect(() => { load(); }, [load]);

  const createSection = async () => {
    const trimmedTitle = newTitle.trim();
    const normalizedFamily = (newFamily.trim() || cmsTab).trim();
    if (!trimmedTitle) {
      setValidationError("Veuillez renseigner un titre de section.");
      return;
    }
    setValidationError("");
    const res = await fetch(`${apiBase}/sections`, { method: "POST", headers: headers(), body: JSON.stringify({ title: trimmedTitle, family: normalizedFamily }) });
    if (res.ok) {
      applyResponse(await res.json());
      setNewTitle(""); setNewFamily(""); setShowNewSection(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      return;
    }
    setValidationError("Impossible de créer la section pour le moment.");
  };

  const updateSection = async (sectionId, data) => {
    const res = await fetch(`${apiBase}/sections/${sectionId}`, { method: "PUT", headers: headers(), body: JSON.stringify(data) });
    if (res.ok) applyResponse(await res.json());
  };

  const deleteSection = async (sectionId) => {
    const res = await fetch(`${apiBase}/sections/${sectionId}`, { method: "DELETE", headers: headers() });
    if (res.ok) applyResponse(await res.json());
  };

  const reorderSections = async (newOrder) => {
    const res = await fetch(`${apiBase}/reorder`, { method: "PUT", headers: headers(), body: JSON.stringify({ order: newOrder }) });
    if (res.ok) applyResponse(await res.json());
  };

  const undo = async () => {
    const res = await fetch(`${apiBase}/undo`, { method: "POST", headers: headers() });
    if (res.ok) applyResponse(await res.json());
  };

  const redo = async () => {
    const res = await fetch(`${apiBase}/redo`, { method: "POST", headers: headers() });
    if (res.ok) applyResponse(await res.json());
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    const reordered = arrayMove(sections, oldIdx, newIdx);
    setSections(reordered);
    reorderSections(reordered.map(s => s.id));
  };

  return (
    <div className="flex flex-col h-full px-3 sm:px-6 pt-2 sm:pt-3 overflow-y-auto" data-testid={testId}>
      {/* Header */}
      <div className="shrink-0 mb-6 flex items-center justify-between">
        <h2 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white">{title}</h2>
        <div className="flex items-center gap-2">
          {editMode && (
            <>
              <button onClick={undo} disabled={!canUndo} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-500 dark:text-gray-400 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Annuler" data-testid="undo-btn">
                <Undo2 className="w-4 h-4" />
              </button>
              <button onClick={redo} disabled={!canRedo} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-500 dark:text-gray-400 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Revenir" data-testid="redo-btn">
                <Redo2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => { setEditMode(e => !e); setShowNewSection(false); setValidationError(""); }}
            className={`p-2 rounded-lg border transition-colors ${editMode ? "border-[#4c7e55] bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.24)] text-[#4c7e55] dark:text-[#2e6d3b]" : "border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
            title={editMode ? "Quitter le mode édition" : "Mode édition"}
            data-testid="edit-mode-btn"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-8 pb-10">
        {editMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map((sec, idx) => (
                <SortableSectionCard key={sec.id} section={sec} index={idx} editMode={editMode} updateSection={updateSection} deleteSection={deleteSection} sensors={sensors} cmsTab={cmsTab} />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <>
            {sections.map((sec, idx) => (
              <SectionCard key={sec.id} section={sec} index={idx} editMode={false} updateSection={updateSection} deleteSection={deleteSection} sensors={sensors} cmsTab={cmsTab} />
            ))}
            {sections.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white/60 dark:bg-[#1a1a1a]/70 px-6 py-10 text-center" data-testid={`${testId}-empty-state`}>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Aucune section n’est encore créée pour cet onglet.</p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Activez le mode édition pour ajouter vos premières sections, listes et checklists.</p>
                <button
                  onClick={() => { setEditMode(true); setShowNewSection(true); setValidationError(""); if (cmsTab !== "Consignes") setNewFamily(cmsTab); }}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#4c7e55] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#416b49] dark:bg-[#2e6d3b] dark:hover:bg-[#255731]"
                  data-testid="empty-state-create-section-btn"
                >
                  <Plus className="h-4 w-4" /> Créer une première section
                </button>
              </div>
            )}
          </>
        )}

        {/* Add section (edit mode only) */}
        {editMode && (
          showNewSection ? (
            <div className="border-2 border-dashed border-[#bfd1c3] dark:border-[#2e6d3b] rounded-xl p-6 bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)]" data-testid="new-section-form">
              <div className="flex flex-col gap-3">
                {cmsTab === "Consignes" ? (
                  <input type="text" value={newFamily} onChange={e => setNewFamily(e.target.value)} placeholder="Famille (optionnel)" className="bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg px-3 py-2" data-testid="new-section-family" />
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-testid="new-section-family-preview">Famille appliquée : {cmsTab}</div>
                )}
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Titre de la section" className="bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg px-3 py-2" data-testid="new-section-title" onKeyDown={e => e.key === 'Enter' && createSection()} />
                {validationError && <p className="text-sm font-medium text-red-600 dark:text-red-400" data-testid="new-section-error">{validationError}</p>}
                <div className="flex gap-2">
                  <button onClick={createSection} className="bg-[#4c7e55] hover:bg-[#416b49] dark:bg-[#2e6d3b] dark:hover:bg-[#255731] text-white text-sm font-bold py-2 px-4 rounded-lg" data-testid="confirm-new-section">Créer</button>
                  <button onClick={() => { setShowNewSection(false); setNewTitle(""); setNewFamily(""); setValidationError(""); }} className="text-gray-500 dark:text-gray-400 text-sm py-2 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="cancel-new-section">Annuler</button>
                </div>
              </div>
            </div>
          ) : (
            <button onClick={() => { setShowNewSection(true); setValidationError(""); if (cmsTab !== "Consignes") setNewFamily(cmsTab); }} className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 text-gray-400 dark:text-gray-500 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors flex items-center justify-center gap-2 text-sm font-bold" data-testid="add-section-btn">
              <Plus className="w-5 h-5" /> Ajouter une section
            </button>
          )
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/* ── Sortable wrapper for sections ── */
function SortableSectionCard(props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: props.section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position: "relative", zIndex: isDragging ? 50 : "auto" };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <SectionCard {...props} dragHandleRef={setActivatorNodeRef} dragListeners={listeners} />
    </div>
  );
}

/* ── Section Card ── */
function SectionCard({ section, index, editMode, updateSection, deleteSection, sensors, dragHandleRef, dragListeners, cmsTab }) {
  const num = String(index + 1).padStart(2, '0');

  const addBox = (type, width) => {
    const newBox = { id: uid(), type, width, urgency: null, title: null, items: [] };
    updateSection(section.id, { boxes: [...section.boxes, newBox] });
  };

  const updateBox = (boxId, data) => {
    const boxes = section.boxes.map(b => b.id === boxId ? { ...b, ...data } : b);
    updateSection(section.id, { boxes });
  };

  const deleteBox = (boxId) => {
    updateSection(section.id, { boxes: section.boxes.filter(b => b.id !== boxId) });
  };

  const handleBoxDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = section.boxes.findIndex(b => b.id === active.id);
    const newIdx = section.boxes.findIndex(b => b.id === over.id);
    const reordered = arrayMove(section.boxes, oldIdx, newIdx);
    updateSection(section.id, { boxes: reordered });
  };

  return (
    <section className="relative bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg overflow-hidden" data-testid={`section-${section.id}`}>
      {/* Section header */}
      <div className="relative px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
        {editMode && (
          <div className="absolute right-3 top-3 flex items-center gap-1">
            {dragHandleRef && (
              <button ref={dragHandleRef} {...(dragListeners || {})} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing transition-colors" data-testid={`drag-section-${section.id}`}>
                <GripVertical className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => deleteSection(section.id)} className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid={`delete-section-${section.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="absolute right-6 bottom-2 text-[4rem] font-black text-gray-100 dark:text-gray-800/50 leading-none select-none pointer-events-none">{num}</div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#4c7e55] dark:text-[#2e6d3b] mb-1">{section.family}</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{section.title}</h3>
      </div>

      {/* Boxes */}
      <div className="p-6">
        {editMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBoxDragEnd}>
            <SortableContext items={section.boxes.map(b => b.id)} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-4 mb-4">
                {section.boxes.map(box => (
                  <SortableBoxCard key={box.id} box={box} editMode={editMode} updateBox={updateBox} deleteBox={deleteBox} sensors={sensors} cmsTab={cmsTab} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          section.boxes.length > 0 && <BoxGrid boxes={section.boxes} editMode={false} updateBox={updateBox} deleteBox={deleteBox} sensors={sensors} cmsTab={cmsTab} />
        )}
        {editMode && <AddBoxBar addBox={addBox} />}
      </div>
    </section>
  );
}

/* ── Box Grid (read mode only) ── */
function BoxGrid({ boxes, editMode, updateBox, deleteBox, sensors, cmsTab }) {
  const rows = [];
  let i = 0;
  while (i < boxes.length) {
    if (boxes[i].width === "half" && i + 1 < boxes.length && boxes[i + 1].width === "half") {
      rows.push([boxes[i], boxes[i + 1]]);
      i += 2;
    } else {
      rows.push([boxes[i]]);
      i++;
    }
  }
  return (
    <div className="flex flex-col gap-4 mb-4">
      {rows.map((row, ri) => (
        <div key={ri} className={`grid gap-4 ${row.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
          {row.map(box => <BoxCard key={box.id} box={box} editMode={editMode} updateBox={updateBox} deleteBox={deleteBox} sensors={sensors} cmsTab={cmsTab} />)}
        </div>
      ))}
    </div>
  );
}

/* ── Sortable wrapper for boxes ── */
function SortableBoxCard(props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: props.box.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position: "relative", zIndex: isDragging ? 50 : "auto" };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <BoxCard {...props} dragHandleRef={setActivatorNodeRef} dragListeners={listeners} />
    </div>
  );
}

/* ── Box Card ── */
function BoxCard({ box, editMode, updateBox, deleteBox, sensors, dragHandleRef, dragListeners, cmsTab }) {
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemText, setNewItemText] = useState("");
  const [showUrgencyPicker, setShowUrgencyPicker] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(box.title || "");

  const urgencyStyle = box.urgency ? URGENCIES.find(u => u.label === box.urgency)?.color || "" : "";

  const addItem = () => {
    if (!newItemText.trim()) return;
    const newItem = { id: uid(), text: newItemText.trim(), status: "idle" };
    updateBox(box.id, { items: [...box.items, newItem] });
    setNewItemText(""); setShowAddItem(false);
  };

  const updateItem = (itemId, data) => {
    updateBox(box.id, { items: box.items.map(it => it.id === itemId ? { ...it, ...data } : it) });
  };

  const deleteItem = (itemId) => {
    updateBox(box.id, { items: box.items.filter(it => it.id !== itemId) });
  };

  const saveTitle = () => {
    updateBox(box.id, { title: titleDraft.trim() || null });
    setEditTitle(false);
  };

  const handleItemDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = box.items.findIndex(it => it.id === active.id);
    const newIdx = box.items.findIndex(it => it.id === over.id);
    const reordered = arrayMove(box.items, oldIdx, newIdx);
    updateBox(box.id, { items: reordered });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#242424] overflow-hidden" data-testid={`box-${box.id}`}>
      {/* Box header */}
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Urgency badge */}
          {box.urgency ? (
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${urgencyStyle}`}>{box.urgency}</span>
              {editMode && (
                <button onClick={() => updateBox(box.id, { urgency: null })} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-urgency-${box.id}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : editMode ? (
            <button onClick={() => setShowUrgencyPicker(!showUrgencyPicker)} className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] mb-1.5 font-bold uppercase tracking-wider" data-testid={`add-urgency-${box.id}`}>
              + Étiquette
            </button>
          ) : null}
          {editMode && showUrgencyPicker && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {URGENCIES.map(u => (
                <button key={u.label} onClick={() => { updateBox(box.id, { urgency: u.label }); setShowUrgencyPicker(false); }} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border cursor-pointer hover:opacity-80 ${u.color}`}>
                  {u.label}
                </button>
              ))}
            </div>
          )}
          {/* Title */}
          {editMode && editTitle ? (
            <input type="text" value={titleDraft} onChange={e => setTitleDraft(e.target.value)} onBlur={saveTitle} onKeyDown={e => e.key === 'Enter' && saveTitle()} className="text-sm font-bold text-gray-900 dark:text-white bg-transparent border-b border-[#4c7e55] dark:border-[#2e6d3b] outline-none w-full mb-1" autoFocus data-testid={`edit-title-${box.id}`} />
          ) : box.title ? (
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`text-sm font-bold text-gray-900 dark:text-white ${editMode ? "cursor-pointer hover:text-[#4c7e55] dark:hover:text-[#2e6d3b]" : ""}`} onClick={() => editMode && setEditTitle(true)}>{box.title}</span>
              {editMode && (
                <button onClick={() => updateBox(box.id, { title: null })} className="text-gray-400 hover:text-red-500" data-testid={`remove-title-${box.id}`}><X className="w-3 h-3" /></button>
              )}
            </div>
          ) : editMode ? (
            <button onClick={() => { setTitleDraft(""); setEditTitle(true); }} className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] mb-1 font-bold uppercase tracking-wider" data-testid={`add-title-${box.id}`}>+ Titre</button>
          ) : null}
        </div>
        {editMode && (
          <div className="flex items-center gap-0.5 shrink-0">
            {dragHandleRef && (
              <button ref={dragHandleRef} {...(dragListeners || {})} className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing transition-colors" data-testid={`drag-box-${box.id}`}>
                <GripVertical className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => deleteBox(box.id)} className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid={`delete-box-${box.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="px-4 pb-3 flex flex-col gap-1">
        {editMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
            <SortableContext items={box.items.map(it => it.id)} strategy={verticalListSortingStrategy}>
              {box.items.map(item => (
                <SortableItemRow key={item.id} item={item} box={box} editMode={editMode} updateItem={updateItem} deleteItem={deleteItem} cmsTab={cmsTab} />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          box.items.map(item => (
            <ItemRow key={item.id} item={item} box={box} editMode={false} updateItem={updateItem} deleteItem={deleteItem} cmsTab={cmsTab} />
          ))
        )}

        {/* Add item (edit mode only) */}
        {editMode && (
          showAddItem ? (
            <div className="flex items-center gap-2 mt-1">
              <input type="text" value={newItemText} onChange={e => setNewItemText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()} placeholder="Texte de l'élément..." className="flex-1 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white rounded px-2.5 py-1.5" autoFocus data-testid={`new-item-input-${box.id}`} />
              <button onClick={addItem} className="text-[#4c7e55] dark:text-[#2e6d3b] hover:text-[#416b49] dark:hover:text-[#255731] text-xs font-bold py-1.5 px-2" data-testid={`confirm-item-${box.id}`}>OK</button>
              <button onClick={() => { setShowAddItem(false); setNewItemText(""); }} className="text-gray-400 hover:text-gray-600 text-xs py-1.5 px-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAddItem(true)} className="text-xs text-gray-400 dark:text-gray-500 hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] font-bold mt-1 flex items-center gap-1" data-testid={`add-item-${box.id}`}>
              <Plus className="w-3.5 h-3.5" /> Ajouter un élément
            </button>
          )
        )}
      </div>
    </div>
  );
}

/* ── Sortable wrapper for items ── */
function SortableItemRow(props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: props.item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ItemRow {...props} dragHandleRef={setActivatorNodeRef} dragListeners={listeners} />
    </div>
  );
}

/* ── Item Row ── */
function ItemRow({ item, box, editMode, updateItem, deleteItem, dragHandleRef, dragListeners, cmsTab }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);

  const save = () => { updateItem(item.id, { text: draft.trim() || item.text }); setEditing(false); };

  if (box.type === "checklist") {
    return (
      <div className="flex items-center gap-2 group" data-testid={`item-${item.id}`}>
        {editMode && dragHandleRef && (
          <button ref={dragHandleRef} {...(dragListeners || {})} className="p-0.5 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing hover:text-gray-500 transition-colors shrink-0" data-testid={`drag-item-${item.id}`}>
            <GripVertical className="w-3 h-3" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          {editMode && editing ? (
            <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} className="text-sm text-gray-900 dark:text-white bg-transparent border-b border-[#4c7e55] dark:border-[#2e6d3b] outline-none w-full" autoFocus />
          ) : (
            <TaskRow taskId={item.id} label={item.text} tab={cmsTab}>
              <span className={`text-sm ${editMode ? "cursor-pointer hover:text-[#4c7e55] dark:hover:text-[#2e6d3b]" : ""}`} onClick={() => editMode && setEditing(true)}>{item.text}</span>
            </TaskRow>
          )}
        </div>
        {editMode && (
          <button onClick={() => deleteItem(item.id)} className="p-0.5 rounded text-gray-300 dark:text-gray-600 group-hover:text-red-400 hover:text-red-500 transition-colors shrink-0" data-testid={`delete-item-${item.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  // List type
  return (
    <div className="flex items-center gap-2 group py-0.5" data-testid={`item-${item.id}`}>
      {editMode && dragHandleRef && (
        <button ref={dragHandleRef} {...(dragListeners || {})} className="p-0.5 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing hover:text-gray-500 transition-colors shrink-0" data-testid={`drag-item-${item.id}`}>
          <GripVertical className="w-3 h-3" />
        </button>
      )}
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-600 shrink-0" />
      {editMode && editing ? (
        <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && save()} className="flex-1 text-sm text-gray-900 dark:text-white bg-transparent border-b border-[#4c7e55] dark:border-[#2e6d3b] outline-none" autoFocus />
      ) : (
        <span className={`flex-1 text-sm text-gray-700 dark:text-gray-300 ${editMode ? "cursor-pointer hover:text-[#4c7e55] dark:hover:text-[#2e6d3b]" : ""}`} onClick={() => editMode && setEditing(true)}>{item.text}</span>
      )}
      {editMode && (
        <button onClick={() => deleteItem(item.id)} className="p-0.5 rounded text-gray-300 dark:text-gray-600 group-hover:text-red-400 hover:text-red-500 transition-colors shrink-0" data-testid={`delete-item-${item.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

/* ── Add Box Bar (edit mode only) ── */
function AddBoxBar({ addBox }) {
  const [open, setOpen] = useState(false);
  return open ? (
    <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4">
      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Ajouter une box</p>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { addBox("checklist", "full"); setOpen(false); }} className="text-xs font-bold py-2.5 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-700 dark:text-gray-300 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors" data-testid="add-checklist-full">Checklist pleine largeur</button>
        <button onClick={() => { addBox("checklist", "half"); setOpen(false); }} className="text-xs font-bold py-2.5 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-700 dark:text-gray-300 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors" data-testid="add-checklist-half">Checklist &times; 2</button>
        <button onClick={() => { addBox("list", "full"); setOpen(false); }} className="text-xs font-bold py-2.5 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-700 dark:text-gray-300 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors" data-testid="add-list-full">Liste pleine largeur</button>
        <button onClick={() => { addBox("list", "half"); setOpen(false); }} className="text-xs font-bold py-2.5 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424] text-gray-700 dark:text-gray-300 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors" data-testid="add-list-half">Liste &times; 2</button>
      </div>
      <button onClick={() => setOpen(false)} className="text-xs text-gray-400 mt-2 hover:text-gray-600">Annuler</button>
    </div>
  ) : (
    <button onClick={() => setOpen(true)} className="w-full border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg py-3 text-gray-400 dark:text-gray-500 hover:border-[#4c7e55] dark:hover:border-[#2e6d3b] hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors flex items-center justify-center gap-1.5 text-xs font-bold" data-testid="add-box-btn">
      <Plus className="w-4 h-4" /> Ajouter une box
    </button>
  );
}
